import { Skeleton } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import tinycolor from "tinycolor2";
import DocumentLinesIcon from "../../assets/icons/document-lines.svg?react";
import { cn } from "../../lib/utils";
import PlaylistThumbnail from "./PlaylistThumbnail";

export type PlaylistCardPlaylist = {
  id: string;
  title: string;
  headerTitle: string | null;
  images: { url: string; sizes: { medium: { file: string } } | null }[];
  metrics: { articles: number };
  room: { primaryColor: string | null; secondaryColor: string | null } | null;
};

export type PlaylistCardVariant = "light" | "dark" | "in-room";

type PlaylistCardVariantProps =
  | { variant: "light" }
  | { variant: "dark" }
  | {
      variant: "in-room";
      primaryColor: string | null;
      secondaryColor: string | null;
    };

type PlaylistCardProps =
  | ({ loading: true } & PlaylistCardVariantProps)
  | ({
      loading?: false;
      playlist: PlaylistCardPlaylist;
      onClick: () => void;
    } & PlaylistCardVariantProps);

const VARIANT_CLASSES = {
  light: "bg-ds-slate-900 text-ds-white border-transparent",
  dark: "bg-ds-slate-50 text-ds-black border-transparent",
  "in-room": "bg-white/15 text-ds-white border-white/[0.18]",
} as const satisfies Record<PlaylistCardVariant, string>;

function roomColorStyle(secondaryColor: string): React.CSSProperties {
  const isLightBg = tinycolor(secondaryColor).isLight();
  return {
    backgroundColor: secondaryColor,
    borderColor: "transparent",
    color: isLightBg ? "var(--color-ds-black)" : "var(--color-ds-white)",
  };
}

function getCardStyle(
  playlist: PlaylistCardPlaylist | null,
  variantProps: PlaylistCardVariantProps
): React.CSSProperties | undefined {
  const playlistRoomColor =
    playlist === null
      ? null
      : playlist.room === null
        ? null
        : playlist.room.secondaryColor;
  if (playlistRoomColor !== null) return roomColorStyle(playlistRoomColor);
  if (
    variantProps.variant === "in-room" &&
    variantProps.secondaryColor !== null
  ) {
    return roomColorStyle(variantProps.secondaryColor);
  }
  return undefined;
}

function getThumbnailColor(
  playlist: PlaylistCardPlaylist,
  variantProps: PlaylistCardVariantProps
): string | null {
  if (playlist.room !== null && playlist.room.primaryColor !== null) {
    return playlist.room.primaryColor;
  }
  if (variantProps.variant === "in-room") return variantProps.primaryColor;
  return null;
}

export default function PlaylistCard(props: PlaylistCardProps) {
  if (props.loading === true) return <PlaylistCardSkeleton {...props} />;
  return <PlaylistCardContent {...props} />;
}

function PlaylistCardContent(
  props: {
    playlist: PlaylistCardPlaylist;
    onClick: () => void;
  } & PlaylistCardVariantProps
) {
  const { playlist, onClick, variant } = props;
  const { t } = useTranslation();
  const image = playlist.images.at(0);
  const imageSrc =
    image === undefined
      ? undefined
      : image.sizes === null
        ? image.url
        : image.sizes.medium.file;
  const [imageHasError, setImageHasError] = useState(false);

  const thumbnail =
    imageSrc === undefined || imageHasError ? (
      <PlaylistThumbnail backgroundColor={getThumbnailColor(playlist, props)} />
    ) : (
      <img
        src={imageSrc}
        alt=""
        className="size-full object-cover"
        onError={() => {
          setImageHasError(true);
        }}
      />
    );

  const publicationCount = (
    <div className="flex shrink-0 items-center gap-1 opacity-90">
      <DocumentLinesIcon className="size-3" />
      <span className="text-[11px]/4 tracking-[-0.11px] whitespace-nowrap">
        {t("{{count}} publications", { count: playlist.metrics.articles })}
      </span>
    </div>
  );

  // The overline. `headerTitle` is the admin-authored heading ("Sélection du Pr X") — when it
  // is set it REPLACES the generic content-type label rather than sitting beside
  // it, because the thumbnail and the publication count already say this is a
  // collection. When it is unset the card names its own type instead of leaving
  // a hole, so an attributed collection is distinguishable at a glance from a
  // standard thematic one.
  //
  // Empty string counts as unset. The API coerces "" → null on write, but
  // legacy rows can still hold "", and a bare `!== null` check would render an
  // empty line — which in the fixed-height card silently steals a line
  // from the title. `PlaylistPageHeader` guards the same way on the detail page.
  const resolvedHeaderTitle =
    playlist.headerTitle !== null && playlist.headerTitle.trim() !== ""
      ? playlist.headerTitle
      : t("Collection");

  // Single line, ellipsised. NOT wrapped on purpose: the card is a fixed
  // height (h-28 / md:h-20) and its title is already `line-clamp-3`, so a second
  // overline line would come straight out of the title's space.
  const overline = (
    <p className="shrink-0 truncate text-[11px]/4 tracking-[-0.11px]">
      {resolvedHeaderTitle}
    </p>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "font-dm-sans flex h-28 md:h-20 w-full cursor-pointer items-start overflow-hidden rounded-2xl border border-solid text-left",
        VARIANT_CLASSES[variant]
      )}
      style={getCardStyle(playlist, props)}
    >
      <div className="h-full w-24 md:w-20 shrink-0 overflow-hidden">
        {thumbnail}
      </div>
      <div className="relative flex h-full min-w-0 flex-1 flex-col gap-1.5 overflow-hidden px-4 py-2 md:py-0.5">
        {overline}
        <div className="flex min-h-0 flex-1 items-center overflow-hidden">
          <p className="line-clamp-3 text-[13px] md:text-[11px] leading-[1.2] font-semibold tracking-[-0.13px]">
            {playlist.title}
          </p>
        </div>
        <div className="flex justify-end">{publicationCount}</div>
      </div>
    </button>
  );
}

function PlaylistCardSkeleton(props: PlaylistCardVariantProps) {
  const { variant } = props;
  const skeletonBg =
    variant === "dark" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.12)";

  return (
    <div
      className={cn(
        "font-dm-sans flex h-28 md:h-20 w-full items-start overflow-hidden rounded-2xl border border-solid",
        VARIANT_CLASSES[variant]
      )}
      style={getCardStyle(null, props)}
    >
      <Skeleton
        variant="rectangular"
        width={96}
        height="100%"
        className="shrink-0"
        sx={{ bgcolor: skeletonBg }}
      />
      <div className="flex h-full min-w-0 flex-1 flex-col gap-1.5 px-4 py-2">
        <Skeleton variant="text" width="40%" sx={{ bgcolor: skeletonBg }} />
        <Skeleton variant="text" width="90%" sx={{ bgcolor: skeletonBg }} />
        <Skeleton variant="text" width="70%" sx={{ bgcolor: skeletonBg }} />
        <Skeleton
          variant="text"
          width={60}
          className="self-end"
          sx={{ bgcolor: skeletonBg }}
        />
      </div>
    </div>
  );
}
